/**
 * Thinccc Design System - Button Component
 *
 * Variants from Figma:
 * - Style: filled, outlined, text, elevated, tonal
 * - Level: primary, secondary, tertiary
 * - State: enabled, hovered, focused, pressed, disabled
 * - With/without icons (leading or trailing)
 */
import React from "react";

import PropTypes from "prop-types";

// ===========================================
// VARIANT STYLES
// ===========================================
const variantStyles = {
  // Filled buttons
  filled: {
    primary: `
      bg-persimmon text-white
      hover:bg-red-400 hover:shadow-elevation-2
      active:bg-red-500
      focus-visible:focus-ring
      disabled:bg-state-disabled disabled:text-white/38 disabled:cursor-not-allowed
    `,
    secondary: `
      bg-charlotte text-background-blackpearl
      hover:bg-blue-300 hover:shadow-elevation-2
      active:bg-blue-400
      focus-visible:focus-ring
      disabled:bg-state-disabled disabled:text-white/38 disabled:cursor-not-allowed
    `,
    tertiary: `
      bg-background-biscay text-white
      hover:bg-background-astronaut hover:shadow-elevation-2
      active:bg-background-cloudburst
      focus-visible:focus-ring
      disabled:bg-state-disabled disabled:text-white/38 disabled:cursor-not-allowed
    `,
  },

  // Outlined buttons
  outlined: {
    primary: `
      bg-transparent border-2 border-persimmon text-persimmon
      hover:bg-persimmon/8 hover:border-red-400
      active:bg-persimmon/12
      focus-visible:focus-ring
      disabled:border-state-disabled disabled:text-white/38 disabled:cursor-not-allowed
    `,
    secondary: `
      bg-transparent border-2 border-charlotte text-charlotte
      hover:bg-charlotte/8 hover:border-blue-300
      active:bg-charlotte/12
      focus-visible:focus-ring
      disabled:border-state-disabled disabled:text-white/38 disabled:cursor-not-allowed
    `,
    tertiary: `
      bg-transparent border-2 border-text-lv4 text-text-lv2
      hover:bg-white/8 hover:border-text-lv3
      active:bg-white/12
      focus-visible:focus-ring
      disabled:border-state-disabled disabled:text-white/38 disabled:cursor-not-allowed
    `,
  },

  // Text buttons (no background)
  text: {
    primary: `
      bg-transparent text-persimmon
      hover:bg-persimmon/8
      active:bg-persimmon/12
      focus-visible:focus-ring
      disabled:text-white/38 disabled:cursor-not-allowed
    `,
    secondary: `
      bg-transparent text-charlotte
      hover:bg-charlotte/8
      active:bg-charlotte/12
      focus-visible:focus-ring
      disabled:text-white/38 disabled:cursor-not-allowed
    `,
    tertiary: `
      bg-transparent text-text-lv2
      hover:bg-white/8
      active:bg-white/12
      focus-visible:focus-ring
      disabled:text-white/38 disabled:cursor-not-allowed
    `,
  },

  // Elevated buttons (with shadow)
  elevated: {
    primary: `
      bg-background-cloudburst text-persimmon shadow-elevation-1
      hover:bg-background-biscay hover:shadow-elevation-2
      active:bg-background-astronaut active:shadow-elevation-1
      focus-visible:focus-ring
      disabled:bg-state-disabled disabled:text-white/38 disabled:shadow-none disabled:cursor-not-allowed
    `,
    secondary: `
      bg-background-cloudburst text-charlotte shadow-elevation-1
      hover:bg-background-biscay hover:shadow-elevation-2
      active:bg-background-astronaut active:shadow-elevation-1
      focus-visible:focus-ring
      disabled:bg-state-disabled disabled:text-white/38 disabled:shadow-none disabled:cursor-not-allowed
    `,
    tertiary: `
      bg-background-cloudburst text-text-lv1 shadow-elevation-1
      hover:bg-background-biscay hover:shadow-elevation-2
      active:bg-background-astronaut active:shadow-elevation-1
      focus-visible:focus-ring
      disabled:bg-state-disabled disabled:text-white/38 disabled:shadow-none disabled:cursor-not-allowed
    `,
  },

  // Tonal buttons (subtle colored background)
  tonal: {
    primary: `
      bg-persimmon/20 text-red-100
      hover:bg-persimmon/30
      active:bg-persimmon/40
      focus-visible:focus-ring
      disabled:bg-state-disabled disabled:text-white/38 disabled:cursor-not-allowed
    `,
    secondary: `
      bg-charlotte/20 text-blue-100
      hover:bg-charlotte/30
      active:bg-charlotte/40
      focus-visible:focus-ring
      disabled:bg-state-disabled disabled:text-white/38 disabled:cursor-not-allowed
    `,
    tertiary: `
      bg-white/8 text-text-lv1
      hover:bg-white/12
      active:bg-white/16
      focus-visible:focus-ring
      disabled:bg-state-disabled disabled:text-white/38 disabled:cursor-not-allowed
    `,
  },
};

// ===========================================
// SIZE STYLES
// ===========================================
const sizeStyles = {
  sm: "h-8 px-3 text-sm gap-1.5 rounded-lg",
  md: "h-10 px-6 text-base gap-2 rounded-full",
  lg: "h-12 px-8 text-md gap-2.5 rounded-full",
  xl: "h-14 px-10 text-md gap-3 rounded-full",
};

// ===========================================
// BUTTON COMPONENT
// ===========================================
const Button = React.forwardRef(
  (
    {
      children,
      variant = "filled",
      level = "primary",
      size = "md",
      leadingIcon,
      trailingIcon,
      fullWidth = false,
      loading = false,
      disabled = false,
      className = "",
      onClick,
      type = "button",
      ...props
    },
    ref,
  ) => {
    const isDisabled = disabled || loading;

    // Get variant + level styles
    const variantLevelStyles =
      variantStyles[variant]?.[level] || variantStyles.filled.primary;

    // Combine all classes
    const buttonClasses = `
    inline-flex items-center justify-center
    font-medium tracking-button
    transition-all duration-fast ease-standard
    select-none
    ${sizeStyles[size]}
    ${variantLevelStyles}
    ${fullWidth ? "w-full" : ""}
    ${className}
  `
      .replace(/\s+/g, " ")
      .trim();

    return (
      <button
        ref={ref}
        type={type}
        className={buttonClasses}
        disabled={isDisabled}
        onClick={onClick}
        {...props}
      >
        {/* Loading spinner */}
        {loading && (
          <svg
            className="animate-spin -ml-1 mr-2 h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
        )}

        {/* Leading icon */}
        {!loading && leadingIcon && (
          <span className="flex-shrink-0">{leadingIcon}</span>
        )}

        {/* Button text */}
        <span>{children}</span>

        {/* Trailing icon */}
        {trailingIcon && <span className="flex-shrink-0">{trailingIcon}</span>}
      </button>
    );
  },
);

Button.displayName = "Button";

// ===========================================
// PROP TYPES
// ===========================================
Button.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(["filled", "outlined", "text", "elevated", "tonal"]),
  level: PropTypes.oneOf(["primary", "secondary", "tertiary"]),
  size: PropTypes.oneOf(["sm", "md", "lg", "xl"]),
  leadingIcon: PropTypes.node,
  trailingIcon: PropTypes.node,
  fullWidth: PropTypes.bool,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  onClick: PropTypes.func,
  type: PropTypes.oneOf(["button", "submit", "reset"]),
};

export default Button;
